import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Instagram, Globe, Apple, Play } from 'lucide-react';
import Logo from './Logo';
import WaitlistForm from './WaitlistForm';
import PhoneMockup from './PhoneMockup';

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 16} height={props.size || 16} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 16} height={props.size || 16} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIALS = [
  { name: 'Instagram', href: 'https://www.instagram.com/getdineora', Icon: Instagram, testId: 'social-link-instagram' },
  { name: 'X', href: 'https://x.com/dineora_ca', Icon: XIcon, testId: 'social-link-twitter' },
  { name: 'TikTok', href: 'https://www.tiktok.com/@dineora.ca', Icon: TikTokIcon, testId: 'social-link-tiktok' },
  { name: 'dineora.ca', href: 'https://dineora.ca', Icon: Globe, testId: 'social-link-web' },
];

const lineReveal = {
  hidden: { y: '115%' },
  show: (i) => ({
    y: '0%',
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.55 + i * 0.14 },
  }),
};

const Hero = ({ refCode, total, onJoined }) => {
  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 600], [0, 140]);
  const phoneY = useTransform(scrollY, [0, 600], [0, -60]);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      <div className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-6" data-testid="main-header-nav">
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <Logo className="h-6 sm:h-7" />
        </motion.div>
        <motion.a
          href="#restaurants"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          data-testid="header-join-restaurant-btn"
          className="hidden sm:inline-flex items-center rounded-full border border-black/15 hover:border-[#f15a24]/70 hover:text-[#f15a24] text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          For restaurants
        </motion.a>
      </div>

      <motion.div
        aria-hidden="true"
        style={{ y: glowY }}
        className="hero-glow pointer-events-none absolute left-1/2 top-[12%] -translate-x-1/2 w-[820px] h-[820px] rounded-full"
      >
        <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(241,90,36,0.16)_0%,rgba(241,90,36,0.06)_40%,transparent_68%)]" />
        <div className="absolute inset-[18%] rounded-full border border-[#f15a24]/10" />
        <div className="absolute inset-[30%] rounded-full border border-[#f15a24]/10" />
      </motion.div>

      <div className="relative z-10 flex-1 flex items-center px-6 sm:px-10 pb-16 pt-4">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-8 items-center">
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-[#f15a24]/30 bg-[#f15a24]/10 px-5 py-2.5"
              data-testid="hero-coming-soon"
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f15a24] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f15a24]" />
              </span>
              <span className="font-mono2 text-xs sm:text-sm font-semibold uppercase tracking-[0.35em] text-[#f15a24]">
                The app · Coming soon
              </span>
            </motion.div>

            <h1
              className="font-display font-medium text-[#171512] text-2xl sm:text-3xl leading-[1.15] max-w-xl lg:whitespace-nowrap"
              data-testid="hero-headline"
            >
              {['The neighbourhood table, in your pocket.'].map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    custom={i}
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 max-w-xl text-[#6e6a63] text-base sm:text-lg leading-relaxed"
            >
              The DineOra app is coming to iOS and Android. Order directly from
              the independent restaurants you already love — no middleman, no
              markups. Be first through the door.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 w-full max-w-md"
            >
              <WaitlistForm refCode={refCode} onJoined={onJoined} />
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.55 }}
              className="mt-5 text-sm text-[#71716b]"
              data-testid="hero-waitlist-count"
            >
              {total > 0
                ? `Join ${total.toLocaleString()}+ food lover${total === 1 ? '' : 's'} already waiting`
                : 'Be the very first through the door'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.65 }}
              className="mt-6 flex flex-wrap justify-center lg:justify-start gap-3"
            >
              <div
                data-testid="badge-app-store"
                className="flex items-center gap-2.5 bg-[#111110] text-white rounded-2xl px-4 py-2.5"
              >
                <Apple size={20} aria-hidden="true" />
                <span className="text-left leading-tight">
                  <span className="block text-[9px] uppercase tracking-wider text-white/60">Coming soon on the</span>
                  <span className="block text-sm font-semibold">App Store</span>
                </span>
              </div>
              <div
                data-testid="badge-google-play"
                className="flex items-center gap-2.5 bg-[#111110] text-white rounded-2xl px-4 py-2.5"
              >
                <Play size={18} aria-hidden="true" />
                <span className="text-left leading-tight">
                  <span className="block text-[9px] uppercase tracking-wider text-white/60">Get it soon on</span>
                  <span className="block text-sm font-semibold">Google Play</span>
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.8 }}
              className="mt-7 flex items-center gap-3"
            >
              {SOCIALS.map(({ name, href, Icon, testId }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={name}
                  data-testid={testId}
                  className="w-11 h-11 rounded-full border border-black/10 bg-white shadow-sm flex items-center justify-center text-[#6e6a63] hover:text-[#f15a24] hover:border-[#f15a24]/50 transition-colors duration-300"
                >
                  <Icon size={16} />
                </a>
              ))}
            </motion.div>
          </div>

          <motion.div style={{ y: phoneY }} className="relative flex justify-center lg:justify-end lg:pr-8">
            <PhoneMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
